import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Head1, OtherText } from 'styling/Typography'
import { TabsContainer, Tabs, PageWrapper } from 'styling/GlobalStyles'
import { NavBar } from 'components/NavBar/NavBar'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import {
  AnalyticsPageWrapper,
  AnalyticsHeader,
  HeaderLeft,
  HeaderActions,
  BannerWrapper,
  BannerLeft,
  BannerRight,
  BannerChip,
  BannerClose,
  TabContent,
} from './HrAnalytics.styled'
import { OverviewTab } from './tabs/OverviewTab'
import { AttendanceTab } from './tabs/AttendanceTab'
import { ScheduleTab } from './tabs/ScheduleTab'
import { PayrollTab } from './tabs/PayrollTab'
import { LogisticsTab } from './tabs/LogisticsTab'

const TABS = [
  { key: 'overview', label: 'Overview', path: '/hr/analytics' },
  { key: 'attendance', label: 'Attendance', path: '/hr/analytics/attendance' },
  { key: 'schedule', label: 'Schedule', path: '/hr/analytics/schedule' },
  { key: 'payroll', label: 'Payroll', path: '/hr/analytics/payroll' },
  { key: 'logistics', label: 'Logistics', path: '/hr/analytics/logistics' },
]

const getActiveTab = (pathname: string): string => {
  if (pathname.startsWith('/hr/analytics/attendance')) return 'attendance'
  if (pathname.startsWith('/hr/analytics/schedule')) return 'schedule'
  if (pathname.startsWith('/hr/analytics/payroll')) return 'payroll'
  if (pathname.startsWith('/hr/analytics/logistics')) return 'logistics'
  return 'overview'
}

interface TabActionConfig {
  showDateDropdown: boolean
  showFilters: boolean
}

const TAB_ACTIONS: Record<string, TabActionConfig> = {
  overview: { showDateDropdown: true, showFilters: false },
  attendance: { showDateDropdown: true, showFilters: false },
  schedule: { showDateDropdown: true, showFilters: false },
  payroll: { showDateDropdown: true, showFilters: false },
  logistics: { showDateDropdown: true, showFilters: false },
}

const getDefaultRange = (): [Date, Date] => {
  const to = new Date()
  to.setDate(to.getDate() - 1)
  to.setHours(23, 59, 59, 999)
  const from = new Date(to)
  from.setDate(to.getDate() - 29)
  from.setHours(0, 0, 0, 0)
  return [from, to]
}

export const HrAnalytics = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = getActiveTab(location.pathname)
  const actions = TAB_ACTIONS[activeTab] || TAB_ACTIONS.overview

  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [[dateFrom, dateTo], setDateRange] = useState<[Date, Date]>(getDefaultRange())

  const handleTabClick = (path: string) => {
    navigate(path)
  }

  const handleDateRangeChange = (firstDay: Date, lastDay: Date) => {
    if (!firstDay || !lastDay) return
    const endOfYesterday = new Date()
    endOfYesterday.setDate(endOfYesterday.getDate() - 1)
    endOfYesterday.setHours(23, 59, 59, 999)
    const clampedTo = lastDay > endOfYesterday ? endOfYesterday : lastDay
    setDateRange([firstDay, clampedTo])
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'attendance':
        return <AttendanceTab />
      case 'schedule':
        return <ScheduleTab startDate={dateFrom} endDate={dateTo} />
      case 'payroll':
        return <PayrollTab dateFrom={dateFrom} dateTo={dateTo} />
      case 'logistics':
        return <LogisticsTab />
      default:
        return <OverviewTab />
    }
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <AnalyticsPageWrapper>
          <AnalyticsHeader>
            <HeaderLeft>
              <Head1 style={{ fontSize: 30, lineHeight: '40px' }} color='#ff9900'>
                HR Analytics
              </Head1>
              <OtherText grey>
                Historical workforce analytics –{' '}
                <span style={{ color: '#ff9900' }}>Finalized data only</span>
              </OtherText>
            </HeaderLeft>
            <HeaderActions>
              {actions.showDateDropdown && (
                <CalendarInput
                  range
                  placeholder='Select date range'
                  width='250px'
                  defaultValue={{ firstDay: dateFrom, lastDay: dateTo }}
                  maxDate={(() => {
                    const d = new Date()
                    d.setDate(d.getDate() - 1)
                    return d
                  })()}
                  onChange={handleDateRangeChange as never}
                />
              )}
            </HeaderActions>
          </AnalyticsHeader>

          <TabsContainer>
            {TABS.map((tab) => (
              <Tabs
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => handleTabClick(tab.path)}
              >
                {tab.label}
              </Tabs>
            ))}
          </TabsContainer>

          {!bannerDismissed && (
            <BannerWrapper>
              <BannerLeft>✓ All records in this range are finalized</BannerLeft>
              <BannerRight>
                <BannerChip>Finalized data only</BannerChip>
                <BannerClose onClick={() => setBannerDismissed(true)}>✕</BannerClose>
              </BannerRight>
            </BannerWrapper>
          )}

          <TabContent>{renderTabContent()}</TabContent>
        </AnalyticsPageWrapper>
      </PageWrapper>
    </>
  )
}
